import React, { useContext, useState } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { OrgContext } from "contexts/OrgContext";
import NumberOfGPUsFilter from "./Modal/NumberOfGPUsFilter";
import MinimumCPUsFilter from "./Modal/MinimumCPUsFilter";
import MinimumRamFilter from "./Modal/MinimumRamFilter";
import PriceRangeFilter from "./Modal/PriceRangeFilter";
import Button from "components/UI-lib/Button";

interface MoreFiltersPopoverProps {
  selectedGPUCount: string;
  setSelectedGPUCount: (gpuNum: string) => void;
  selectedMinCPUCount: number;
  setSelectedMinCPUCount: (number: number) => void;
  selectMinRam: string;
  setSelectMinRam: (ram: string) => void;
  priceRange: number[];
  setPriceRange: (number: number[]) => void;
}

const MoreFiltersPopover: React.FC<MoreFiltersPopoverProps> = ({
  selectedGPUCount,
  setSelectedGPUCount,
  selectedMinCPUCount,
  setSelectedMinCPUCount,
  selectMinRam,
  setSelectMinRam,
  priceRange,
  setPriceRange,
}) => {
  const orgContext = useContext(OrgContext);

  const resetFilters = () => {
    setSelectedGPUCount("any");
    setSelectedMinCPUCount(0);
    setSelectMinRam("any");
    setPriceRange([0, 100]);
  };

  const getFilterSummary = () => {
    const filters: string[] = [];
    if (selectedGPUCount !== "any")
      filters.push(
        `${selectedGPUCount} GPU${selectedGPUCount !== "1" ? "s" : ""}`
      );
    if (selectMinRam !== "any") filters.push(`${selectMinRam} RAM`);
    if (selectedMinCPUCount > 0) filters.push(`${selectedMinCPUCount} CPUs`);
    if (priceRange[0] !== 0 || priceRange[1] !== 100)
      filters.push(`$${priceRange[0]}-$${priceRange[1]}`);

    if (filters.length === 0) return "No filters";
    if (filters.length === 1) return filters[0];

    return `${filters.length} filters applied`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex ml-2 relative mr-2 animate-border rounded-md">
          <div className="flex relative bg-primaryBg py-2 px-6 min-h-[46px] max-h-[46px] items-center rounded-md border border-zinc-800 cursor-pointer">
            <div className="flex flex-row items-center w-full h-full">
              <div className="flex flex-row">
                <div className="flex flex-row items-center justify-center">
                  <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-900 dark:text-slate-100 mr-1" />
                </div>
                <p className="text-gray-500 dark:text-secondary text-xs mt-[3px] hidden sm:block">
                  {getFilterSummary()}
                </p>
              </div>
            </div>
          </div>
          <span className="btnBefore"></span>
          <span className="btnAfter"></span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-4 min-w-[600px] pb-8"
        side="bottom"
        align="end"
      >
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center mb-4">
            <h1 className="text-white font-bold text-lg">Filters</h1>
            <Button
              onClick={resetFilters}
              label="Reset Filters"
              iconLeft={<ArrowPathIcon className="w-4 h-4 mr-2" />}
            />
          </div>

          <hr className="w-full mb-4 dark:border dark:border-zinc-800" />

          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <h2 className="text-md text-gray-900 dark:text-white font-semibold mb-3">
                Compute Attributes
              </h2>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Number of GPUs
                    </h3>
                  </div>
                  <NumberOfGPUsFilter
                    selectedNumberOfGPUs={selectedGPUCount}
                    setSelectedNumberOfGPUs={setSelectedGPUCount}
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Minimum Ram{selectMinRam === "any" ? "" : ":"}
                    </h3>
                    {selectMinRam !== "any" && (
                      <h3 className="text-sm text-highlight ml-1">
                        at least {selectMinRam}
                      </h3>
                    )}
                  </div>
                  <MinimumRamFilter
                    selectedMinimumRam={selectMinRam}
                    setSelectedMinimumRam={setSelectMinRam}
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Minimum CPUs{selectedMinCPUCount === 0 ? "" : ":"}
                    </h3>
                    {selectedMinCPUCount !== 0 && (
                      <h3 className="text-sm text-highlight ml-1">
                        {selectedMinCPUCount}
                      </h3>
                    )}
                  </div>
                  <MinimumCPUsFilter
                    selectedMinimumNumberOfCPUs={selectedMinCPUCount}
                    setSelectedMinimumNumberOfCPUs={setSelectedMinCPUCount}
                  />
                </div>
              </div>
            </div>

            {orgContext.currencyDisplay === "credits" && (
              <>
                <hr className="w-full dark:border dark:border-zinc-800" />
                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h2 className="text-md text-gray-900 dark:text-white font-semibold">
                      Hourly Price Range
                    </h2>
                    <h3 className="text-sm text-highlight ml-1 mt-[1px]">
                      ${priceRange[0]} - ${priceRange[1]}
                    </h3>
                  </div>
                  <PriceRangeFilter
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MoreFiltersPopover;
